// Outlook Agenda Sync Service — Microsoft Graph API
import { PublicClientApplication } from '@azure/msal-browser';
import { getAzureConfig, CALENDAR_SCOPES, GRAPH_BASE } from '../config/azureConfig';

let msalInstance = null;
let cachedAccount = null;
let cachedCalendarId = null;

// De specifieke agenda waarin verlof wordt geplaatst
const TARGET_CALENDAR_NAME = 'Personeel- Verlofplanning';

// ========== MSAL SETUP ==========

/**
 * Vroege initialisatie — roep aan in index.js zodat MSAL redirect-responses
 * worden afgehandeld voordat de rest van de app laadt.
 * Na Microsoft login redirect terug naar de app, pikt handleRedirectPromise()
 * de auth-response op en slaat het account op in de MSAL cache.
 */
export async function earlyMsalInit() {
  try {
    const instance = getMsalInstance();
    if (!instance) return;

    if (!initPromise) {
      initPromise = (async () => {
        await instance.initialize();
        try {
          const response = await instance.handleRedirectPromise();
          if (response?.account) {
            cachedAccount = response.account;
            console.log('[MSAL] Redirect login succesvol:', response.account.username);
          }
        } catch (e) {
          console.warn('[MSAL] Redirect afhandeling:', e);
        }
      })();
    }
    await initPromise;
    console.log('[MSAL] Early init OK, accounts:', instance.getAllAccounts().length);
  } catch (e) {
    console.warn('[MSAL] Early init skip:', e.message);
  }
}

function getMsalInstance() {
  const config = getAzureConfig();
  if (!config?.clientId || !config?.tenantId) return null;

  if (!msalInstance || msalInstance._clientId !== config.clientId) {
    msalInstance = new PublicClientApplication({
      auth: {
        clientId: config.clientId,
        authority: `https://login.microsoftonline.com/${config.tenantId}`,
        redirectUri: window.location.origin,
      },
      cache: { cacheLocation: 'localStorage', storeAuthStateInCookie: false },
    });
    msalInstance._clientId = config.clientId;
    cachedAccount = null;
    initPromise = null; // Reset zodat initialize() opnieuw wordt aangeroepen
  }
  return msalInstance;
}

let initPromise = null;

async function ensureInitialized() {
  const instance = getMsalInstance();
  if (!instance) throw new Error('Azure niet geconfigureerd');

  if (!initPromise) {
    initPromise = (async () => {
      await instance.initialize();
      try {
        const response = await instance.handleRedirectPromise();
        if (response?.account) {
          cachedAccount = response.account;
        }
      } catch (e) {
        console.warn('MSAL redirect afhandeling:', e);
      }
    })();
  }
  await initPromise;
  return instance;
}

// ========== AUTH ==========

export async function connectOutlook() {
  const instance = await ensureInitialized();

  // Check eerst of er al een account is (bijv. na redirect)
  const accounts = instance.getAllAccounts();
  if (accounts.length > 0) {
    cachedAccount = accounts[0];
    return {
      success: true,
      account: accounts[0].username,
      name: accounts[0].name,
    };
  }

  // Start redirect login — navigeert weg van de app
  try {
    await instance.loginRedirect({
      scopes: CALENDAR_SCOPES,
      prompt: 'select_account',
    });
    // Dit punt wordt nooit bereikt — browser navigeert weg
    return { success: false, error: 'Redirect gestart...' };
  } catch (err) {
    console.error('Outlook login mislukt:', err);
    if (err.errorCode === 'interaction_in_progress') {
      // Verwijder vastgelopen interactie-state
      const keys = Object.keys(sessionStorage);
      keys.forEach(key => {
        if (key.startsWith('msal.') && key.includes('interaction')) {
          sessionStorage.removeItem(key);
        }
      });
      return { success: false, error: 'Vorige login hing vast — probeer opnieuw.' };
    }
    if (err.errorCode === 'consent_required') {
      return { success: false, error: 'Toestemming vereist. Klik "Grant admin consent" in Azure Portal → API machtigingen.' };
    }
    return { success: false, error: `${err.errorCode || 'Onbekend'}: ${err.message}` };
  }
}

export async function disconnectOutlook() {
  try {
    const instance = await ensureInitialized();
    const accounts = instance.getAllAccounts();
    for (const account of accounts) {
      instance.getTokenCache?.()?.removeAccount?.(account);
    }
  } catch (e) {
    console.warn('MSAL logout:', e);
  }
  // Altijd lokale state opruimen
  cachedAccount = null;
  cachedCalendarId = null;
  initPromise = null;
  msalInstance = null;
  // Verwijder MSAL cache uit storage
  const keys = [...Object.keys(localStorage), ...Object.keys(sessionStorage)];
  keys.forEach(key => {
    if (key.startsWith('msal.') || key.includes('msal')) {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    }
  });
}

export async function getOutlookStatus() {
  try {
    const instance = await ensureInitialized();
    const accounts = instance.getAllAccounts();
    if (accounts.length === 0) return { connected: false };
    cachedAccount = accounts[0];
    // Probeer silent token te halen (geen popup)
    try {
      const response = await instance.acquireTokenSilent({
        scopes: CALENDAR_SCOPES,
        account: accounts[0],
      });
      if (!response?.accessToken) return { connected: false };
    } catch {
      return { connected: false };
    }
    // Probeer agenda op te zoeken
    let calendarName = null;
    try {
      const calId = await getTargetCalendarId();
      if (calId) calendarName = TARGET_CALENDAR_NAME;
    } catch { /* agenda check is optioneel */ }
    return { connected: true, account: accounts[0].username, name: accounts[0].name, calendarName };
  } catch {
    return { connected: false };
  }
}

async function getAccessToken() {
  const instance = await ensureInitialized();
  const accounts = instance.getAllAccounts();
  if (accounts.length === 0) return null;

  try {
    const response = await instance.acquireTokenSilent({
      scopes: CALENDAR_SCOPES,
      account: cachedAccount || accounts[0],
    });
    return response.accessToken;
  } catch (err) {
    console.warn('Silent token mislukt, redirect nodig:', err?.errorCode);
    // Token verlopen — redirect voor nieuwe login
    try {
      await instance.acquireTokenRedirect({
        scopes: CALENDAR_SCOPES,
        account: cachedAccount || accounts[0],
      });
    } catch (redirectErr) {
      console.error('Token redirect mislukt:', redirectErr);
    }
    return null;
  }
}

async function graphFetch(path, options = {}) {
  const token = await getAccessToken();
  if (!token) throw new Error('Niet ingelogd bij Outlook');

  const response = await fetch(`${GRAPH_BASE}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err?.error?.message || `Graph API fout: ${response.status}`);
  }

  if (response.status === 204) return null; // No content (delete)
  return response.json();
}

// ========== AGENDA OPZOEKEN ==========

/**
 * Zoek de agenda "Personeel- Verlofplanning" op, of maak deze aan als die niet bestaat.
 * Cached het ID zodat we niet elke keer opnieuw hoeven te zoeken.
 */
async function getTargetCalendarId() {
  if (cachedCalendarId) return cachedCalendarId;

  try {
    // Zoek alle agenda's van de ingelogde gebruiker
    const result = await graphFetch('/me/calendars?$top=50');
    const calendars = result?.value || [];

    // Zoek op exacte naam
    let target = calendars.find(c => c.name === TARGET_CALENDAR_NAME);

    if (!target) {
      // Probeer case-insensitive
      target = calendars.find(c => c.name.toLowerCase() === TARGET_CALENDAR_NAME.toLowerCase());
    }

    if (target) {
      cachedCalendarId = target.id;
      console.log(`📅 Agenda gevonden: "${target.name}" (${target.id.substring(0, 20)}...)`);
      return cachedCalendarId;
    }

    // Agenda bestaat niet → maak hem aan
    console.log(`📅 Agenda "${TARGET_CALENDAR_NAME}" niet gevonden, wordt aangemaakt...`);
    const newCal = await graphFetch('/me/calendars', {
      method: 'POST',
      body: JSON.stringify({ name: TARGET_CALENDAR_NAME }),
    });

    if (newCal?.id) {
      cachedCalendarId = newCal.id;
      console.log(`📅 Agenda aangemaakt: "${newCal.name}"`);
      return cachedCalendarId;
    }

    throw new Error('Agenda kon niet worden aangemaakt');
  } catch (err) {
    console.error('Agenda opzoeken/aanmaken mislukt:', err);
    // Fallback: gebruik standaard agenda
    return null;
  }
}

/**
 * Geeft het juiste Graph API pad terug voor events in de doelagenda.
 * Als de specifieke agenda niet gevonden wordt, valt terug op /me/events.
 */
async function getEventsPath() {
  const calId = await getTargetCalendarId();
  if (calId) return `/me/calendars/${calId}/events`;
  return '/me/events'; // fallback
}

// ========== AGENDA OPERATIES ==========

const DAG_KEYS = ['zo', 'ma', 'di', 'wo', 'do', 'vr', 'za'];

/**
 * Haal werk start- en eindtijd op uit het werkrooster voor een specifieke datum.
 * Retourneert {startTijd: "08:30", eindTijd: "17:00"} of null als vrije dag.
 */
function getWerktijden(medewerker, dateStr) {
  if (!medewerker?.Werkrooster) {
    // Geen rooster → standaard werktijden
    const dow = new Date(dateStr + 'T12:00:00').getDay();
    if (dow === 0 || dow === 6) return null; // weekend
    return { startTijd: '08:30', eindTijd: '17:00' };
  }

  const dow = new Date(dateStr + 'T12:00:00').getDay();
  const dagKey = DAG_KEYS[dow]; // zo=0, ma=1, ...
  const rooster = medewerker.Werkrooster[dagKey];
  if (!rooster) return null; // Vrije dag

  // Parse alle blokken (ochtend, middag, avond) en vind vroegste start / laatste eind
  let vroegste = null;
  let laatste = null;

  for (const blok of ['ochtend', 'middag', 'avond']) {
    const tijdStr = rooster[blok]; // bijv. "08:30-12:30"
    if (!tijdStr || !tijdStr.includes('-')) continue;
    const [start, eind] = tijdStr.split('-').map(t => t.trim());
    if (!start || !eind) continue;
    if (!vroegste || start < vroegste) vroegste = start;
    if (!laatste || eind > laatste) laatste = eind;
  }

  if (!vroegste || !laatste) return null;
  return { startTijd: vroegste, eindTijd: laatste };
}

/**
 * Zorg dat er een Outlook-categorie bestaat per medewerker met de juiste kleur.
 * Cached welke categorieën al zijn aangemaakt.
 */
const createdCategories = new Set();

async function ensureMedewerkerCategory(naam, appKleur) {
  if (!naam || createdCategories.has(naam)) return naam;

  const outlookColor = mapAppColorToOutlook(appKleur);

  try {
    // Probeer aan te maken
    await graphFetch('/me/outlook/masterCategories', {
      method: 'POST',
      body: JSON.stringify({ displayName: naam, color: outlookColor }),
    });
  } catch (err) {
    if (err.message?.includes('409') || err.message?.includes('already exists')) {
      // Categorie bestaat al — update de kleur
      try {
        const encodedNaam = encodeURIComponent(naam);
        await graphFetch(`/me/outlook/masterCategories('${encodedNaam}')`, {
          method: 'PATCH',
          body: JSON.stringify({ color: outlookColor }),
        });
      } catch (patchErr) {
        console.warn(`Categorie kleur update "${naam}":`, patchErr.message);
      }
    } else {
      console.warn(`Categorie "${naam}" aanmaken:`, err.message);
    }
  }

  createdCategories.add(naam);
  return naam;
}

/**
 * Status-categorieën: vaste kleuren voor Aanwezig/Ziek/Afwezig bolletjes in Outlook.
 */
const STATUS_CATEGORIES = {
  aanwezig:  { naam: 'Aanwezig',  color: 'preset4' },  // Groen
  ziek:      { naam: 'Ziek',      color: 'preset1' },  // Oranje
  ingediend: { naam: 'Ingediend', color: 'preset1' },  // Oranje
  afwezig:   { naam: 'Afwezig',   color: 'preset0' },  // Rood
};

async function ensureStatusCategories() {
  for (const cat of Object.values(STATUS_CATEGORIES)) {
    if (createdCategories.has(cat.naam)) continue;
    try {
      await graphFetch('/me/outlook/masterCategories', {
        method: 'POST',
        body: JSON.stringify({ displayName: cat.naam, color: cat.color }),
      });
    } catch (err) {
      if (err.message?.includes('409') || err.message?.includes('already exists')) {
        try {
          const enc = encodeURIComponent(cat.naam);
          await graphFetch(`/me/outlook/masterCategories('${enc}')`, {
            method: 'PATCH',
            body: JSON.stringify({ color: cat.color }),
          });
        } catch (_) { /* ignore */ }
      }
    }
    createdCategories.add(cat.naam);
  }
}

/**
 * Bepaal de status-categorie op basis van verlof.
 */
function getStatusCategory(isZiek, isVerlof, isIngediend) {
  if (isZiek) return STATUS_CATEGORIES.ziek.naam;
  if (isVerlof && isIngediend) return STATUS_CATEGORIES.ingediend.naam;
  if (isVerlof) return STATUS_CATEGORIES.afwezig.naam;
  return STATUS_CATEGORIES.aanwezig.naam;
}

/**
 * Map app medewerker-kleuren naar Outlook preset category kleuren.
 * Outlook heeft 25 preset kleuren (preset0-preset24).
 * Voor custom kleuren: vindt de dichtsbijzijnde preset kleur.
 */
function mapAppColorToOutlook(hexColor) {
  // Exact match first
  const exactMap = {
    '#0078D4': 'preset7',   // Blue
    '#107C10': 'preset4',   // Green
    '#8764B8': 'preset8',   // Purple
    '#E86E1C': 'preset1',   // Orange
    '#D13438': 'preset0',   // Red
    '#00B7C3': 'preset5',   // Teal
    '#744DA9': 'preset8',   // Purple (dark)
    '#498205': 'preset6',   // Olive
    '#DA3B01': 'preset9',   // Cranberry
    '#0063B1': 'preset7',   // Blue (dark)
  };
  const upper = (hexColor || '').toUpperCase();
  if (exactMap[upper]) return exactMap[upper];

  // Outlook preset kleuren (approximate RGB values)
  const presets = [
    { preset: 'preset0', r: 211, g: 52, b: 56 },    // Red
    { preset: 'preset1', r: 232, g: 110, b: 28 },   // Orange
    { preset: 'preset2', r: 183, g: 142, b: 58 },    // Brown/Peach
    { preset: 'preset3', r: 255, g: 185, b: 0 },     // Yellow
    { preset: 'preset4', r: 16, g: 124, b: 16 },     // Green
    { preset: 'preset5', r: 0, g: 183, b: 195 },     // Teal
    { preset: 'preset6', r: 73, g: 130, b: 5 },      // Olive
    { preset: 'preset7', r: 0, g: 120, b: 212 },     // Blue
    { preset: 'preset8', r: 135, g: 100, b: 184 },   // Purple
    { preset: 'preset9', r: 218, g: 59, b: 1 },      // Cranberry
    { preset: 'preset10', r: 132, g: 117, b: 69 },   // Steel
    { preset: 'preset11', r: 64, g: 64, b: 64 },     // DarkSteel
    { preset: 'preset12', r: 128, g: 128, b: 128 },  // Gray
    { preset: 'preset13', r: 74, g: 84, b: 89 },     // DarkGray
    { preset: 'preset14', r: 0, g: 0, b: 0 },        // Black
    { preset: 'preset15', r: 167, g: 29, b: 49 },    // DarkRed
    { preset: 'preset16', r: 227, g: 116, b: 0 },    // DarkOrange
    { preset: 'preset17', r: 130, g: 107, b: 0 },    // DarkBrown
    { preset: 'preset18', r: 191, g: 144, b: 0 },    // DarkYellow
    { preset: 'preset19', r: 0, g: 104, b: 43 },     // DarkGreen
    { preset: 'preset20', r: 0, g: 128, b: 128 },    // DarkTeal
    { preset: 'preset21', r: 54, g: 96, b: 0 },      // DarkOlive
    { preset: 'preset22', r: 0, g: 75, b: 141 },     // DarkBlue
    { preset: 'preset23', r: 104, g: 33, b: 122 },   // DarkPurple
    { preset: 'preset24', r: 142, g: 35, b: 72 },    // DarkCranberry
  ];

  // Parse hex to RGB
  const hex = upper.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16) || 0;
  const g = parseInt(hex.substring(2, 4), 16) || 0;
  const b = parseInt(hex.substring(4, 6), 16) || 0;

  // Find closest by Euclidean distance
  let closest = 'preset7';
  let minDist = Infinity;
  for (const p of presets) {
    const dist = (r - p.r) ** 2 + (g - p.g) ** 2 + (b - p.b) ** 2;
    if (dist < minDist) { minDist = dist; closest = p.preset; }
  }
  return closest;
}

/**
 * Genereer alle datums tussen start en eind (inclusief).
 */
function getDateRange(startStr, endStr) {
  const dates = [];
  const start = new Date(startStr + 'T12:00:00');
  const end = new Date((endStr || startStr) + 'T12:00:00');
  const current = new Date(start);
  while (current <= end) {
    dates.push(current.toISOString().split('T')[0]);
    current.setDate(current.getDate() + 1);
  }
  return dates;
}

/**
 * Maak agenda-events aan voor een verlofaanvraag.
 * Multi-dag verlof wordt opgesplitst in aparte events per werkdag,
 * zodat elk event op de Outlook tijdbalk verschijnt met de juiste werktijden.
 *
 * @returns {string|null} Komma-gescheiden event IDs (bijv. "id1,id2,id3")
 */
export async function createCalendarEvent(verlof, status, verlofType, medewerker, medewerkerKleur) {
  if (!isConnected()) return null;

  const isGoedgekeurd = status === 'goedgekeurd';
  const startDate = verlof.StartDatum || verlof.startDatum;
  const endDate = verlof.EindDatum || verlof.eindDatum || startDate;
  const naam = verlof.MedewerkerNaam || verlof.medewerkerNaam;
  const uren = verlof.Uren || verlof.uren || 0;
  const typeLabel = verlofType?.label || 'V';
  const typeText = verlofType?.text || 'Verlof';

  // Categorie per medewerker (kleur in Outlook)
  let categories = [];
  if (naam && medewerkerKleur) {
    try {
      const catName = await ensureMedewerkerCategory(naam, medewerkerKleur);
      categories = [catName];
    } catch { /* niet kritiek */ }
  }

  // Status categorie (bolletje: groen/oranje/rood)
  try {
    await ensureStatusCategories();
    const isZiek = verlof.Type === 'ziek' || verlof.type === 'ziek';
    const statusCat = getStatusCategory(isZiek, true, !isGoedgekeurd);
    categories.push(statusCat);
  } catch { /* niet kritiek */ }

  const showAs = isGoedgekeurd ? 'oof' : 'tentative';
  const bodyContent = `${typeText} voor ${naam}\nUren: ${uren}\nStatus: ${isGoedgekeurd ? 'Goedgekeurd' : 'Ingediend'}`;
  const eventsPath = await getEventsPath();
  const eventIds = [];

  // Expliciete tijden (dagdeel aanvraag op 1 dag)?
  const explStartTijd = verlof.StartTijd || verlof.beginTijd;
  const explEindTijd = verlof.EindTijd || verlof.eindTijd;

  if (explStartTijd && explEindTijd && startDate === (endDate || startDate)) {
    // Enkele dag met expliciete tijden
    const subject = `${typeLabel} ${naam} (${typeText}${uren ? ` - ${uren}u` : ''})`;
    try {
      const result = await graphFetch(eventsPath, {
        method: 'POST',
        body: JSON.stringify({
          subject, isAllDay: false, showAs, categories,
          start: { dateTime: `${startDate}T${explStartTijd}:00`, timeZone: 'Europe/Amsterdam' },
          end: { dateTime: `${startDate}T${explEindTijd}:00`, timeZone: 'Europe/Amsterdam' },
          body: { contentType: 'text', content: bodyContent },
        }),
      });
      if (result?.id) eventIds.push(result.id);
    } catch (err) { console.error('Event aanmaken mislukt:', err); }
    return eventIds.length > 0 ? eventIds.join(',') : null;
  }

  // Multi-dag of single-dag: maak per werkdag een event
  const dates = getDateRange(startDate, endDate);

  for (const dateStr of dates) {
    const werktijden = getWerktijden(medewerker, dateStr);
    if (!werktijden) continue; // Vrije dag → geen event

    const subject = `${typeLabel} ${naam} (${typeText}${uren ? ` - ${uren}u` : ''})`;

    try {
      const result = await graphFetch(eventsPath, {
        method: 'POST',
        body: JSON.stringify({
          subject, isAllDay: false, showAs, categories,
          start: { dateTime: `${dateStr}T${werktijden.startTijd}:00`, timeZone: 'Europe/Amsterdam' },
          end: { dateTime: `${dateStr}T${werktijden.eindTijd}:00`, timeZone: 'Europe/Amsterdam' },
          body: { contentType: 'text', content: bodyContent },
        }),
      });
      if (result?.id) eventIds.push(result.id);
    } catch (err) {
      console.error(`Event aanmaken mislukt voor ${dateStr}:`, err);
    }

    // Rate limiting
    if (eventIds.length % 4 === 0) await new Promise(r => setTimeout(r, 100));
  }

  return eventIds.length > 0 ? eventIds.join(',') : null;
}

/**
 * Update bestaande agenda-events.
 * Verwijdert alle oude events en maakt nieuwe aan (simpelste aanpak voor multi-dag).
 * @returns {string|null} Nieuwe komma-gescheiden event IDs
 */
export async function updateCalendarEvent(eventId, verlof, status, verlofType, medewerker, medewerkerKleur) {
  if (!isConnected() || !eventId) return false;

  // Verwijder alle oude events
  await deleteCalendarEvent(eventId);

  // Maak nieuwe events aan
  const newIds = await createCalendarEvent(verlof, status, verlofType, medewerker, medewerkerKleur);
  return newIds || false;
}

/**
 * Verwijder agenda-events (ondersteunt komma-gescheiden IDs voor multi-dag events)
 */
export async function deleteCalendarEvent(eventId) {
  if (!isConnected() || !eventId) return false;

  const ids = eventId.split(',').map(id => id.trim()).filter(Boolean);
  const eventsPath = await getEventsPath();
  let success = true;

  for (const id of ids) {
    try {
      await graphFetch(`${eventsPath}/${id}`, { method: 'DELETE' });
    } catch (err) {
      // 404 = al verwijderd, dat is OK
      if (!err.message?.includes('404')) {
        console.error(`Event ${id.substring(0, 20)}... verwijderen mislukt:`, err);
        success = false;
      }
    }
  }

  return success;
}

// ========== HELPERS ==========

/**
 * Sync alle verlofaanvragen naar Outlook agenda.
 * @param {Array} verlofaanvragen - Alle verlofaanvragen
 * @param {Array} verlofTypes - Verloftype definities
 * @param {Array} medewerkers - Alle medewerkers (voor werkrooster + kleur)
 * @param {Object} medewerkerKleurMap - {medewerkerID: '#hex'} kleur mapping
 * @param {Function} onProgress - Callback (current, total, item)
 * @param {Function} updateVerlofaanvraag - Firestore update functie
 * @returns {{ synced, created, updated, deleted, errors }}
 */
export async function syncAllToOutlook(verlofaanvragen, verlofTypes, medewerkers, medewerkerKleurMap, onProgress, updateVerlofaanvraag) {
  if (!isConnected()) throw new Error('Niet verbonden met Outlook');

  const stats = { synced: 0, created: 0, updated: 0, deleted: 0, errors: 0 };
  const currentYear = new Date().getFullYear();

  // Filter: alleen dit jaar, goedgekeurd of ingediend (inclusief open ziek)
  const teSync = verlofaanvragen.filter(v =>
    v.jaar === currentYear &&
    (v.status === 'goedgekeurd' || v.status === 'ingediend' || v.isOpenZiek)
  );

  // Items die afgekeurd zijn maar nog een calendarEventId hebben → verwijderen
  const teVerwijderen = verlofaanvragen.filter(v =>
    v.calendarEventId &&
    (v.status === 'afgekeurd' || v.status === 'ingetrokken')
  );

  const totaal = teSync.length + teVerwijderen.length;
  let teller = 0;

  // Verwijder events voor afgekeurde/ingetrokken items
  for (const v of teVerwijderen) {
    teller++;
    if (onProgress) onProgress(teller, totaal, { naam: v.medewerkerNaam, actie: 'verwijderen' });
    try {
      await deleteCalendarEvent(v.calendarEventId);
      if (updateVerlofaanvraag) await updateVerlofaanvraag(v.id, { calendarEventId: null });
      stats.deleted++;
      stats.synced++;
    } catch (err) {
      console.error(`Sync delete mislukt voor ${v.id}:`, err);
      stats.errors++;
    }
  }

  // Sync goedgekeurde/ingediende items
  for (const v of teSync) {
    teller++;
    const vt = verlofTypes.find(t => t.key === v.type);
    const mw = medewerkers.find(m => m.Id === v.medewerkerId);
    const kleur = medewerkerKleurMap?.[v.medewerkerId] || '#0078D4';
    if (onProgress) onProgress(teller, totaal, { naam: v.medewerkerNaam, type: vt?.text || v.type, actie: v.calendarEventId ? 'bijwerken' : 'aanmaken' });

    try {
      if (v.calendarEventId) {
        // Delete oude events + maak nieuwe aan
        const newIds = await updateCalendarEvent(v.calendarEventId, v, v.status, vt, mw, kleur);
        if (newIds && updateVerlofaanvraag) {
          await updateVerlofaanvraag(v.id, { calendarEventId: newIds });
        }
        stats.updated++;
      } else {
        const eventId = await createCalendarEvent(v, v.status, vt, mw, kleur);
        if (eventId && updateVerlofaanvraag) {
          await updateVerlofaanvraag(v.id, { calendarEventId: eventId });
        }
        stats.created++;
      }
      stats.synced++;
    } catch (err) {
      console.error(`Sync mislukt voor ${v.id}:`, err);
      stats.errors++;
    }

    // Vertraging — meer events per aanvraag door per-dag splitting
    if (teller % 3 === 0) await new Promise(r => setTimeout(r, 500));
  }

  return stats;
}

// ========== HELPERS (internal) ==========

/**
 * Verwijder ALLE events uit de Outlook agenda (voor reset & resync).
 * Haalt events op in batches en verwijdert ze.
 * @returns {number} Aantal verwijderde events
 */
export async function purgeAllCalendarEvents(onProgress) {
  if (!isConnected()) throw new Error('Niet verbonden met Outlook');

  const eventsPath = await getEventsPath();
  let deleted = 0;
  let hasMore = true;
  let page = 0;

  while (hasMore) {
    page++;
    if (onProgress) onProgress({ fase: 'ophalen', pagina: page, verwijderd: deleted });

    // Haal batch op (max 50 per keer)
    const result = await graphFetch(`${eventsPath}?$top=50&$select=id,subject&$orderby=start/dateTime`);
    const events = result?.value || [];

    if (events.length === 0) { hasMore = false; break; }

    for (const ev of events) {
      try {
        await graphFetch(`${eventsPath}/${ev.id}`, { method: 'DELETE' });
        deleted++;
        if (onProgress) onProgress({ fase: 'verwijderen', verwijderd: deleted, huidig: ev.subject });
      } catch (err) {
        if (!err.message?.includes('404')) {
          console.warn(`Event verwijderen mislukt: ${ev.subject}`, err);
        }
      }
      // Rate limiting
      if (deleted % 4 === 0) await new Promise(r => setTimeout(r, 100));
    }

    // Als er minder dan 50 kwamen, zijn we klaar
    if (events.length < 50) hasMore = false;
  }

  return deleted;
}

// ========== HELPERS (date) ==========

function addDays(dateStr, days) {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

export function isConnected() {
  try {
    const instance = getMsalInstance();
    if (!instance) return false;
    return instance.getAllAccounts().length > 0;
  } catch {
    return false;
  }
}

/** Synchrone status check — voor SystemCheck component (geen API calls) */
export function getOutlookStatusSync() {
  try {
    const config = getAzureConfig();
    const configured = !!(config.clientId && config.tenantId);
    const connected = isConnected();
    return { configured, connected };
  } catch {
    return { configured: false, connected: false };
  }
}

/**
 * Sync werkrooster + beschikbaarheid naar Outlook.
 * Maakt per medewerker per werkdag één event aan dat het volledige dagbeeld toont:
 * werkrooster als basis, met verlof/ziek/flex overlays.
 *
 * Events worden in een aparte categorie "Werkrooster" geplaatst zodat ze
 * visueel onderscheidbaar zijn van verlof-events.
 *
 * @param {Object} params
 * @param {Array} params.medewerkers - Actieve medewerkers
 * @param {Array} params.verlofaanvragen - Alle verlofaanvragen
 * @param {Array} params.roosterWijzigingen - Roosterwijzigingen
 * @param {Array} params.feestdagen - Feestdagen
 * @param {Object} params.medewerkerKleurMap - Kleur per medewerker ID
 * @param {number} params.weken - Aantal weken vooruit (default: 4)
 * @param {Function} params.onProgress - Progress callback
 * @returns {Object} { created, skipped, errors }
 */
export async function syncBeschikbaarheidToOutlook({ medewerkers, verlofaanvragen, roosterWijzigingen, feestdagen, medewerkerKleurMap, weken = 4, totEind, onProgress }) {
  if (!isConnected()) throw new Error('Niet verbonden met Outlook');

  // Reset category cache zodat kleuren altijd worden bijgewerkt
  createdCategories.clear();

  // Zorg dat status-categorieën bestaan (Aanwezig/Ziek/Afwezig bolletjes)
  await ensureStatusCategories();

  const eventsPath = await getEventsPath();
  const stats = { created: 0, skipped: 0, errors: 0, total: 0 };
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Genereer alle datums voor de sync-periode
  const datums = [];
  let eindDatum;
  if (totEind) {
    eindDatum = new Date(totEind + 'T23:59:59');
  } else {
    eindDatum = new Date(today);
    eindDatum.setDate(eindDatum.getDate() + weken * 7);
  }
  const diffDays = Math.ceil((eindDatum - today) / (1000 * 60 * 60 * 24));
  for (let i = 0; i <= diffDays; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() + i);
    datums.push(d.toISOString().split('T')[0]);
  }

  // Zorg dat "Werkrooster" categorie bestaat
  try {
    await graphFetch('/me/outlook/masterCategories', {
      method: 'POST',
      body: JSON.stringify({ displayName: 'Werkrooster', color: 'preset12' }), // Gray
    });
  } catch { /* bestaat al */ }

  // Zorg dat "Ziekmelding" categorie bestaat (oranje)
  try {
    await graphFetch('/me/outlook/masterCategories', {
      method: 'POST',
      body: JSON.stringify({ displayName: 'Ziekmelding', color: 'preset1' }), // Orange
    });
  } catch { /* bestaat al */ }

  const actieveMw = medewerkers.filter(m => (m.Status || 'actief') === 'actief' && m.Rol !== 'flexkracht');
  const feestdagSet = new Set(feestdagen.map(f => f.datum || f.Datum));

  // Tel totaal voor progress
  let totalItems = 0;
  for (const mw of actieveMw) {
    for (const datum of datums) {
      const werktijden = getWerktijden(mw, datum);
      if (werktijden || roosterWijzigingen.some(rw => rw.medewerkerId === mw.Id && rw.datum === datum)) {
        totalItems++;
      }
    }
  }
  stats.total = totalItems;
  let teller = 0;

  for (const mw of actieveMw) {
    const mwKleur = medewerkerKleurMap?.[mw.Id] || '#0078D4';
    const displayNaam = mw.VerkorteNaam || mw.Naam;
    let mwCategorie;
    try {
      mwCategorie = await ensureMedewerkerCategory(displayNaam, mwKleur);
    } catch { mwCategorie = displayNaam; }

    for (const datum of datums) {
      const isFeestdag = feestdagSet.has(datum);
      if (isFeestdag) continue;

      // Check roosterwijzigingen
      const extraWerk = roosterWijzigingen.find(rw => rw.medewerkerId === mw.Id && rw.datum === datum);
      const isVervangen = roosterWijzigingen.some(rw => rw.medewerkerId === mw.Id && rw.vervangDatum === datum);
      
      let werktijden = getWerktijden(mw, datum);
      if (isVervangen) werktijden = null; // Dag is geruild
      if (extraWerk && !werktijden) {
        werktijden = { startTijd: extraWerk.startTijd || '08:30', eindTijd: extraWerk.eindTijd || '17:00' };
      }

      if (!werktijden) continue; // Vrije dag
      teller++;

      // Check verlof op deze dag
      const verlofOpDag = verlofaanvragen.filter(v => {
        if (v.medewerkerId !== mw.Id) return false;
        if (v.status !== 'goedgekeurd' && v.status !== 'ingediend') return false;
        const start = v.startDatum || v.StartDatum;
        const eind = v.eindDatum || v.EindDatum || start;
        if (datum < start) return false;
        // Open ziekmelding: altijd tonen (ook toekomst) — medewerker is nog steeds ziek
        if (v.isOpenZiek) return true;
        return datum <= eind;
      });

      const isZiek = verlofOpDag.some(v => v.type === 'ziek' || v.isOpenZiek);
      const isFlexADV = verlofOpDag.some(v => v.type === 'flexADV' || v.type === 'adv' || v.type === 'flex-min');
      const isVerlof = verlofOpDag.length > 0 && !isZiek;
      const verlofType = verlofOpDag[0]?.type || '';
      const verlofStatus = verlofOpDag[0]?.status || verlofOpDag[0]?.Status || '';
      const isIngediend = verlofStatus === 'ingediend';

      // Bouw subject en details
      let subject, showAs, body;
      const dagLabel = new Date(datum + 'T12:00:00').toLocaleDateString('nl-NL', { weekday: 'short' });

      // Korte afkortingen voor verloftypes
      const typeAfkorting = (type) => {
        const map = { vakantie: 'VAK', ziek: 'ZIEK', bijzonder: 'BV', flex: 'FLEX', 'flex-min': 'FLEX', 'flex-plus': 'FLEX+', flexADV: 'ADV', adv: 'ADV' };
        return map[type] || type?.toUpperCase()?.substring(0, 4) || 'VERLOF';
      };

      if (isZiek) {
        subject = `ZIEK - ${displayNaam}`;
        showAs = 'oof';
        body = `Ziekmelding${verlofOpDag[0]?.startDatum ? ` sinds ${verlofOpDag[0].startDatum}` : ''}`;
      } else if (isFlexADV && isVerlof) {
        const afk = typeAfkorting(verlofType);
        const v = verlofOpDag[0];
        const dagdeel = v.dagdeel || v.Dagdeel;
        if (dagdeel && dagdeel !== 'hele dag') {
          subject = `${afk} - ${displayNaam} (${dagdeel})`;
          showAs = isIngediend ? 'tentative' : 'oof';
          body = `Werkrooster: ${werktijden.startTijd}-${werktijden.eindTijd}\nFlex/ADV (${dagdeel})\nStatus: ${verlofStatus}`;
        } else {
          subject = `${afk} - ${displayNaam}`;
          showAs = isIngediend ? 'tentative' : 'oof';
          body = `Flex/ADV\nStatus: ${verlofStatus}`;
        }
      } else if (isVerlof) {
        const afk = typeAfkorting(verlofType);
        const v = verlofOpDag[0];
        const dagdeel = v.dagdeel || v.Dagdeel;
        if (dagdeel && dagdeel !== 'hele dag') {
          subject = `${afk} - ${displayNaam} (${dagdeel})`;
          showAs = 'tentative';
          body = `Werkrooster: ${werktijden.startTijd}-${werktijden.eindTijd}\nVerlof (${dagdeel})\nStatus: ${verlofStatus}`;
        } else {
          subject = `${afk} - ${displayNaam}`;
          showAs = isIngediend ? 'tentative' : 'oof';
          body = `Verlof\nStatus: ${verlofStatus}`;
        }
      } else if (extraWerk) {
        subject = `RUIL - ${displayNaam}`;
        showAs = 'busy';
        body = `Roosterwijziging: ${extraWerk.reden || 'dag ruil'}\nWerkt ${werktijden.startTijd}-${werktijden.eindTijd}`;
      } else {
        subject = `${displayNaam} ${werktijden.startTijd}-${werktijden.eindTijd}`;
        showAs = 'busy';
        body = `Werkrooster: ${werktijden.startTijd}-${werktijden.eindTijd}`;
      }

      if (onProgress) onProgress({ teller, total: totalItems, naam: mw.Naam, datum, subject });

      try {
        const statusCat = getStatusCategory(isZiek, isVerlof || isFlexADV, isIngediend);
        const eventCategories = isZiek
          ? [mwCategorie, 'Ziekmelding', statusCat]
          : isVerlof || isFlexADV
            ? [mwCategorie, 'Werkrooster', statusCat]
            : [mwCategorie, 'Werkrooster', statusCat];
        await graphFetch(eventsPath, {
          method: 'POST',
          body: JSON.stringify({
            subject,
            isAllDay: false,
            showAs,
            categories: eventCategories,
            start: { dateTime: `${datum}T${werktijden.startTijd}:00`, timeZone: 'Europe/Amsterdam' },
            end: { dateTime: `${datum}T${werktijden.eindTijd}:00`, timeZone: 'Europe/Amsterdam' },
            body: { contentType: 'text', content: body },
          }),
        });
        stats.created++;
      } catch (err) {
        console.warn(`Werkrooster event mislukt: ${mw.Naam} ${datum}:`, err.message);
        stats.errors++;
      }

      // Rate limiting: pause per 5 events
      if (teller % 5 === 0) await new Promise(r => setTimeout(r, 200));
    }
  }

  // ========== CAPACITEITSWAARSCHUWINGEN ==========
  // Voeg rode waarschuwings-events toe op dagen met onvoldoende bezetting
  try {
    await graphFetch('/me/outlook/masterCategories', {
      method: 'POST',
      body: JSON.stringify({ displayName: 'Capaciteitswaarschuwing', color: 'preset11' }), // Red
    });
  } catch { /* bestaat al */ }

  const minimumBezetting = 2; // Standaard minimum
  for (const datum of datums) {
    const isFeestdag = feestdagSet.has(datum);
    if (isFeestdag) continue;
    const d = new Date(datum + 'T12:00:00');
    if (d.getDay() === 0 || d.getDay() === 6) continue;

    // Tel beschikbare medewerkers op deze dag
    let beschikbaar = 0;
    for (const mw of actieveMw) {
      const werktijden = getWerktijden(mw, datum);
      const isVerv = roosterWijzigingen.some(rw => rw.medewerkerId === mw.Id && rw.vervangDatum === datum);
      const extraWerk = roosterWijzigingen.find(rw => rw.medewerkerId === mw.Id && rw.datum === datum);
      let werkt = !!werktijden && !isVerv;
      if (extraWerk) werkt = true;
      if (!werkt) continue;

      const heeftVerlof = verlofaanvragen.some(v => {
        if (v.medewerkerId !== mw.Id) return false;
        if (v.status !== 'goedgekeurd' && v.status !== 'ingediend') return false;
        const start = v.startDatum || v.StartDatum;
        const eind = v.eindDatum || v.EindDatum || start;
        if (datum < start) return false;
        if (v.isOpenZiek) return true; // Ziek = niet beschikbaar (ook toekomst)
        return datum <= eind;
      });
      if (!heeftVerlof) beschikbaar++;
    }

    if (beschikbaar < minimumBezetting && beschikbaar >= 0) {
      teller++;
      if (onProgress) onProgress({ teller, total: totalItems + 1, naam: '⚠️ Capaciteit', datum, subject: `Onderbezetting: ${beschikbaar} beschikbaar` });
      try {
        await graphFetch(eventsPath, {
          method: 'POST',
          body: JSON.stringify({
            subject: `⚠️ ONDERBEZETTING — ${beschikbaar} medewerker${beschikbaar !== 1 ? 's' : ''} beschikbaar`,
            isAllDay: true,
            showAs: 'free',
            categories: ['Capaciteitswaarschuwing', 'Werkrooster'],
            start: { dateTime: `${datum}T00:00:00`, timeZone: 'Europe/Amsterdam' },
            end: { dateTime: `${datum}T23:59:59`, timeZone: 'Europe/Amsterdam' },
            body: { contentType: 'text', content: `Op ${datum} ${beschikbaar === 0 ? 'is er niemand' : `zijn er slechts ${beschikbaar} medewerker(s)`} beschikbaar.\nMinimum bezetting: ${minimumBezetting}\n\nHet boekingssysteem blokkeert automatisch afspraken bij onvoldoende capaciteit.` },
            importance: beschikbaar === 0 ? 'high' : 'normal',
          }),
        });
        stats.created++;
      } catch (err) {
        console.warn(`Capaciteitswaarschuwing mislukt ${datum}:`, err.message);
      }
    }
  }

  return stats;
}

/**
 * Verwijder alle werkrooster-events (categorie "Werkrooster") uit de Outlook agenda.
 * Laat verlof-events ongemoeid.
 */
export async function purgeWerkroosterEvents(onProgress) {
  if (!isConnected()) throw new Error('Niet verbonden met Outlook');

  const eventsPath = await getEventsPath();
  let deleted = 0;
  let hasMore = true;
  let page = 0;

  while (hasMore) {
    page++;
    if (onProgress) onProgress({ fase: 'ophalen', pagina: page, verwijderd: deleted });

    // Haal events op die "Werkrooster" categorie hebben
    const result = await graphFetch(
      `${eventsPath}?$top=50&$filter=categories/any(c:c eq 'Werkrooster')&$select=id,subject&$orderby=start/dateTime`
    );
    const events = result?.value || [];

    if (events.length === 0) { hasMore = false; break; }

    for (const ev of events) {
      try {
        await graphFetch(`${eventsPath}/${ev.id}`, { method: 'DELETE' });
        deleted++;
        if (onProgress) onProgress({ fase: 'verwijderen', verwijderd: deleted, huidig: ev.subject });
      } catch (err) {
        if (!err.message?.includes('404')) console.warn(`Event verwijderen mislukt: ${ev.subject}`, err);
      }
      if (deleted % 4 === 0) await new Promise(r => setTimeout(r, 100));
    }

    if (events.length < 50) hasMore = false;
  }

  return deleted;
}

export async function testConnection() {
  try {
    const me = await graphFetch('/me');
    return { success: true, email: me.mail || me.userPrincipalName, name: me.displayName };
  } catch (err) {
    return { success: false, error: err.message };
  }
}

/**
 * Auto-sync werkrooster naar Outlook (background, silent).
 * Purged bestaande werkrooster-events en maakt nieuwe aan.
 * Bedoeld voor automatische trigger na verlof/ziek/betermelding acties.
 */
export async function autoSyncWerkrooster({ medewerkers, verlofaanvragen, roosterWijzigingen, feestdagen, medewerkerKleurMap }) {
  if (!isConnected()) return;
  try {
    await purgeWerkroosterEvents(() => {});
    await syncBeschikbaarheidToOutlook({
      medewerkers, verlofaanvragen, roosterWijzigingen, feestdagen,
      medewerkerKleurMap, weken: 8,
      onProgress: () => {},
    });
    console.log('✅ Outlook werkrooster auto-sync voltooid (verlofactie)');
  } catch (err) {
    console.warn('Outlook auto-sync mislukt:', err.message);
  }
}

// ═══════════════════════════════════════════════════════════════
// AUTO-SYNC: Individuele verlof/overuren/flex events
// Fire-and-forget functies voor automatische Outlook sync
// ═══════════════════════════════════════════════════════════════

/**
 * Auto-sync single verlof item naar Outlook.
 * Maakt event aan bij goedkeuring, verwijdert bij afkeuring/intrekking.
 * @returns {string|null} calendarEventId (of null als niet gesynct)
 */
export async function autoSyncVerlofEvent(verlof, verlofTypes, medewerkers, medewerkerKleurMap) {
  if (!isConnected()) return null;
  try {
    const status = verlof.status || verlof.Status;
    const vt = verlofTypes.find(t => t.key === (verlof.type || verlof.Type));
    const mw = medewerkers.find(m => (m.Id || m.id) === verlof.medewerkerId);
    const kleur = medewerkerKleurMap?.[verlof.medewerkerId] || '#0078D4';
    const oldEventId = verlof.calendarEventId;

    if (status === 'goedgekeurd' || status === 'ingediend') {
      // Verwijder oude event indien aanwezig, maak nieuwe
      if (oldEventId) {
        try { await deleteCalendarEvent(oldEventId); } catch {}
      }
      const newId = await createCalendarEvent(verlof, status, vt, mw, kleur);
      console.log(`✅ Outlook verlof-event ${oldEventId ? 'bijgewerkt' : 'aangemaakt'}: ${verlof.medewerkerNaam || verlof.MedewerkerNaam}`);
      return newId;
    } else if (status === 'afgekeurd' || status === 'ingetrokken') {
      // Verwijder event
      if (oldEventId) {
        await deleteCalendarEvent(oldEventId);
        console.log(`🗑️ Outlook verlof-event verwijderd: ${verlof.medewerkerNaam || verlof.MedewerkerNaam}`);
      }
      return null;
    }
    return oldEventId || null;
  } catch (err) {
    console.warn('Auto-sync verlof-event mislukt:', err.message);
    return verlof.calendarEventId || null;
  }
}

/**
 * Maak Outlook event voor goedgekeurde overuren.
 * @returns {string|null} calendarEventId
 */
export async function createOverurenEvent(overuren, medewerkerKleurMap) {
  if (!isConnected()) return null;
  try {
    const naam = overuren.medewerkerNaam || overuren.MedewerkerNaam || '';
    const datum = overuren.datum || overuren.Datum;
    const uren = overuren.uren || overuren.Uren || 0;
    const status = overuren.status || overuren.Status || 'ingediend';
    const kleur = medewerkerKleurMap?.[overuren.medewerkerId] || '#E86E1C';

    let categories = [];
    try {
      const catName = await ensureMedewerkerCategory(naam, kleur);
      categories = [catName];
    } catch {}

    // Overuren krijgen eigen categorie (oranje accent)
    try {
      await ensureStatusCategories();
      categories.push('Overuren');
      try {
        await graphFetch('/me/outlook/masterCategories', {
          method: 'POST',
          body: JSON.stringify({ displayName: 'Overuren', color: 'preset4' }), // oranje
        });
      } catch {} // bestaat al
    } catch {}

    const eventsPath = await getEventsPath();
    const startTijd = overuren.startTijd || '17:00';
    const eindTijd = overuren.eindTijd || (uren > 0 ? `${Math.min(17 + Math.ceil(uren), 23)}:00` : '19:00');
    const statusLabel = status === 'goedgekeurd' ? 'Goedgekeurd' : status === 'uitbetaald' ? 'Uitbetaald' : 'Ingediend';
    const statusIcon = status === 'goedgekeurd' ? '✅' : status === 'uitbetaald' ? '💰' : '⏳';
    const subject = `⏰ OU ${naam} (${uren}u) ${statusIcon}`;
    const bodyContent = `Overuren voor ${naam}\nUren: ${uren}\nStatus: ${statusLabel}\n${overuren.omschrijving || overuren.Omschrijving || ''}`;

    const result = await graphFetch(eventsPath, {
      method: 'POST',
      body: JSON.stringify({
        subject,
        isAllDay: false,
        showAs: 'free',
        categories,
        start: { dateTime: `${datum}T${startTijd}:00`, timeZone: 'Europe/Amsterdam' },
        end: { dateTime: `${datum}T${eindTijd}:00`, timeZone: 'Europe/Amsterdam' },
        body: { contentType: 'text', content: bodyContent },
      }),
    });

    console.log(`✅ Outlook overuren-event aangemaakt: ${naam} ${datum} (${statusLabel})`);
    return result?.id || null;
  } catch (err) {
    console.warn('Overuren-event aanmaken mislukt:', err.message);
    return null;
  }
}

/**
 * Auto-sync single overuren item naar Outlook.
 * Maakt event aan bij indienen en goedkeuring, verwijdert bij afkeuring.
 * Bij statuswijziging wordt oud event verwijderd en nieuw aangemaakt (met juiste statuslabel).
 * @returns {string|null} calendarEventId
 */
export async function autoSyncOverurenEvent(overuren, medewerkerKleurMap) {
  if (!isConnected()) return null;
  try {
    const status = overuren.status || overuren.Status;
    const oldEventId = overuren.calendarEventId;

    if (status === 'ingediend' || status === 'goedgekeurd') {
      // Verwijder oud event (als status veranderd) en maak nieuw aan met juiste label
      if (oldEventId) {
        try { await deleteCalendarEvent(oldEventId); } catch {}
      }
      return await createOverurenEvent(overuren, medewerkerKleurMap);
    } else if (status === 'afgekeurd') {
      if (oldEventId) {
        await deleteCalendarEvent(oldEventId);
        console.log(`🗑️ Outlook overuren-event verwijderd`);
      }
      return null;
    }
    // 'uitbetaald' → event behouden
    return oldEventId || null;
  } catch (err) {
    console.warn('Auto-sync overuren-event mislukt:', err.message);
    return overuren.calendarEventId || null;
  }
}

/**
 * Maak Outlook event voor flex planning.
 * @returns {string|null} calendarEventId
 */
export async function createFlexPlanningEvent(planning) {
  if (!isConnected()) return null;
  try {
    const naam = planning.flexkrachtNaam || '';
    const datum = planning.datum;
    const startTijd = planning.startTijd || '09:00';
    const eindTijd = planning.eindTijd || '17:00';
    const uren = planning.uren || 0;

    let categories = [];
    try {
      const catName = await ensureMedewerkerCategory(naam, '#FFB900');
      categories = [catName];
    } catch {}

    // Flex planning categorie (geel)
    try {
      await graphFetch('/me/outlook/masterCategories', {
        method: 'POST',
        body: JSON.stringify({ displayName: 'Flexkracht', color: 'preset3' }), // geel
      });
    } catch {} // bestaat al
    categories.push('Flexkracht');

    const eventsPath = await getEventsPath();
    const subject = `⚡ Flex ${naam} (${uren}u)`;
    const bodyContent = `Flexkracht inplanning\n${naam}: ${startTijd}–${eindTijd} (${uren}u)\n${planning.omschrijving || ''}`;

    const result = await graphFetch(eventsPath, {
      method: 'POST',
      body: JSON.stringify({
        subject,
        isAllDay: false,
        showAs: 'busy',
        categories,
        start: { dateTime: `${datum}T${startTijd}:00`, timeZone: 'Europe/Amsterdam' },
        end: { dateTime: `${datum}T${eindTijd}:00`, timeZone: 'Europe/Amsterdam' },
        body: { contentType: 'text', content: bodyContent },
      }),
    });

    console.log(`✅ Outlook flex-event aangemaakt: ${naam} ${datum}`);
    return result?.id || null;
  } catch (err) {
    console.warn('Flex-event aanmaken mislukt:', err.message);
    return null;
  }
}

/**
 * Auto-sync single flex planning item naar Outlook.
 * Maakt event aan of verwijdert het.
 * @returns {string|null} calendarEventId
 */
export async function autoSyncFlexPlanningEvent(planning, action = 'upsert') {
  if (!isConnected()) return null;
  try {
    const oldEventId = planning.calendarEventId;

    if (action === 'delete') {
      if (oldEventId) {
        await deleteCalendarEvent(oldEventId);
        console.log(`🗑️ Outlook flex-event verwijderd: ${planning.flexkrachtNaam}`);
      }
      return null;
    }

    // Upsert: verwijder oude, maak nieuwe
    if (oldEventId) {
      try { await deleteCalendarEvent(oldEventId); } catch {}
    }
    return await createFlexPlanningEvent(planning);
  } catch (err) {
    console.warn('Auto-sync flex-event mislukt:', err.message);
    return planning.calendarEventId || null;
  }
}

/**
 * Purge mutatie-events (verlof, overuren, flexkracht) uit Outlook.
 * Verwijdert events met categories 'Overuren' en 'Flexkracht',
 * plus alle events referenced door calendarEventId in de meegegeven data.
 */
export async function purgeMutatieEvents({ verlofaanvragen = [], overuren = [], flexPlanning = [], onProgress }) {
  if (!isConnected()) throw new Error('Niet verbonden met Outlook');

  const eventsPath = await getEventsPath();
  let deleted = 0;
  const deletedIds = new Set();

  // Stap 1: Verwijder events via opgeslagen calendarEventIds
  const allIds = [];
  for (const v of verlofaanvragen) {
    if (v.calendarEventId) {
      // Verlof kan meerdere eventIds hebben (comma-separated voor multi-dag)
      const ids = v.calendarEventId.split(',').map(id => id.trim()).filter(Boolean);
      allIds.push(...ids);
    }
  }
  for (const o of overuren) {
    if (o.calendarEventId) allIds.push(o.calendarEventId);
  }
  for (const f of flexPlanning) {
    if (f.calendarEventId) allIds.push(f.calendarEventId);
  }

  if (onProgress) onProgress({ fase: 'events verwijderen via ID', verwijderd: 0, totaal: allIds.length });
  for (const id of allIds) {
    if (deletedIds.has(id)) continue;
    try {
      await graphFetch(`${eventsPath}/${id}`, { method: 'DELETE' });
      deletedIds.add(id);
      deleted++;
    } catch (err) {
      if (!err.message?.includes('404')) console.warn('Event verwijderen mislukt:', err);
    }
    if (onProgress) onProgress({ fase: 'events verwijderen', verwijderd: deleted });
    if (deleted % 4 === 0) await new Promise(r => setTimeout(r, 100));
  }

  // Stap 2: Categorie-gebaseerde cleanup (vangt orphans)
  for (const cat of ['Overuren', 'Flexkracht']) {
    let hasMore = true;
    while (hasMore) {
      if (onProgress) onProgress({ fase: `categorie '${cat}' opruimen`, verwijderd: deleted });
      try {
        const result = await graphFetch(
          `${eventsPath}?$top=50&$filter=categories/any(c:c eq '${cat}')&$select=id,subject`
        );
        const events = result?.value || [];
        if (events.length === 0) { hasMore = false; break; }
        for (const ev of events) {
          if (deletedIds.has(ev.id)) continue;
          try {
            await graphFetch(`${eventsPath}/${ev.id}`, { method: 'DELETE' });
            deletedIds.add(ev.id);
            deleted++;
          } catch {}
          if (deleted % 4 === 0) await new Promise(r => setTimeout(r, 100));
        }
        if (events.length < 50) hasMore = false;
      } catch { hasMore = false; }
    }
  }

  // Stap 3: Verwijder verlof-events die NIET werkrooster zijn (subject check)
  // Verlof-events beginnen met VAK, ZIK, BV, etc. — werkrooster met medewerker-naam
  let hasMore = true;
  let skip = 0;
  while (hasMore) {
    try {
      const result = await graphFetch(
        `${eventsPath}?$top=50&$skip=${skip}&$select=id,subject,categories&$orderby=start/dateTime desc`
      );
      const events = result?.value || [];
      if (events.length === 0) { hasMore = false; break; }
      for (const ev of events) {
        if (deletedIds.has(ev.id)) { continue; }
        const subj = ev.subject || '';
        const cats = ev.categories || [];
        const isWerkrooster = cats.includes('Werkrooster');
        const isMutatie = /^(VAK|ZIK|BV|OBV|VM|VZ|ADV|OU|⏰|⚡|Flex)\s/i.test(subj);
        if (!isWerkrooster && isMutatie) {
          try {
            await graphFetch(`${eventsPath}/${ev.id}`, { method: 'DELETE' });
            deletedIds.add(ev.id);
            deleted++;
          } catch {}
          if (deleted % 4 === 0) await new Promise(r => setTimeout(r, 100));
        }
      }
      skip += events.length;
      if (events.length < 50) hasMore = false;
      if (skip > 2000) hasMore = false; // veiligheidsgrens
    } catch { hasMore = false; }
    if (onProgress) onProgress({ fase: 'verlof-events opruimen', verwijderd: deleted });
  }

  return deleted;
}

/**
 * Bulk sync mutaties (verlof, overuren, flex) naar Outlook.
 * @param {string} vanDatum — ISO datum string (bijv. '2026-01-01')
 * @param {string} totDatum — ISO datum string (bijv. '2026-12-31')
 */
export async function syncBulkMutaties({
  verlofaanvragen, overuren, flexPlanning, verlofTypes, medewerkers,
  medewerkerKleurMap, vanDatum, totDatum, onProgress,
  updateVerlofaanvraagFn, updateOverurenFn, updateFlexPlanningFn,
}) {
  if (!isConnected()) throw new Error('Niet verbonden met Outlook');

  const stats = { verlof: 0, overuren: 0, flex: 0, errors: 0 };
  let teller = 0;

  // Filter items op datum-range
  const van = vanDatum;
  const tot = totDatum;

  const teVerlof = verlofaanvragen.filter(v => {
    const start = v.startDatum || v.StartDatum;
    return start >= van && start <= tot &&
      (v.status === 'goedgekeurd' || v.status === 'ingediend' || v.isOpenZiek);
  });

  const teOveruren = overuren.filter(o => {
    const d = o.datum || o.Datum;
    return d >= van && d <= tot && o.status !== 'afgekeurd';
  });

  const teFlex = flexPlanning.filter(f => {
    const d = f.datum || f.Datum;
    return d >= van && d <= tot;
  });

  const totaal = teVerlof.length + teOveruren.length + teFlex.length;

  // Sync verlof
  for (const v of teVerlof) {
    teller++;
    if (onProgress) onProgress({ teller, total: totaal, item: `Verlof: ${v.medewerkerNaam || v.MedewerkerNaam}` });
    try {
      const vt = verlofTypes.find(t => t.key === (v.type || v.Type));
      const mw = medewerkers.find(m => m.Id === (v.medewerkerId));
      const kleur = medewerkerKleurMap?.[v.medewerkerId] || '#0078D4';

      if (v.calendarEventId) {
        try { const ids = v.calendarEventId.split(','); for (const id of ids) { try { await deleteCalendarEvent(id.trim()); } catch {} } } catch {}
      }
      const eventId = await createCalendarEvent(v, v.status || v.Status, vt, mw, kleur);
      if (eventId && updateVerlofaanvraagFn) {
        await updateVerlofaanvraagFn(v.id || v.Id, { calendarEventId: eventId });
      }
      stats.verlof++;
    } catch (err) {
      console.warn('Verlof sync mislukt:', err.message);
      stats.errors++;
    }
    if (teller % 3 === 0) await new Promise(r => setTimeout(r, 300));
  }

  // Sync overuren
  for (const o of teOveruren) {
    teller++;
    if (onProgress) onProgress({ teller, total: totaal, item: `Overuren: ${o.medewerkerNaam || o.MedewerkerNaam}` });
    try {
      if (o.calendarEventId) {
        try { await deleteCalendarEvent(o.calendarEventId); } catch {}
      }
      const eventId = await createOverurenEvent(o, medewerkerKleurMap);
      if (eventId && updateOverurenFn) {
        await updateOverurenFn(o.id, { calendarEventId: eventId });
      }
      stats.overuren++;
    } catch (err) {
      console.warn('Overuren sync mislukt:', err.message);
      stats.errors++;
    }
    if (teller % 3 === 0) await new Promise(r => setTimeout(r, 300));
  }

  // Sync flex planning
  for (const f of teFlex) {
    teller++;
    if (onProgress) onProgress({ teller, total: totaal, item: `Flex: ${f.medewerkerNaam || f.MedewerkerNaam}` });
    try {
      if (f.calendarEventId) {
        try { await deleteCalendarEvent(f.calendarEventId); } catch {}
      }
      const eventId = await createFlexPlanningEvent(f);
      if (eventId && updateFlexPlanningFn) {
        await updateFlexPlanningFn(f.id, { calendarEventId: eventId });
      }
      stats.flex++;
    } catch (err) {
      console.warn('Flex sync mislukt:', err.message);
      stats.errors++;
    }
    if (teller % 3 === 0) await new Promise(r => setTimeout(r, 300));
  }

  return stats;
}
